import { useMemo } from 'react';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID } from '~/_shared/constants/debugConstants';
import { convertPolygonToGeometry } from '~/_shared/types/polygon/polygon.utils';
import { type PolygonFilterRequest } from '~/spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { useCheckBoundaryIsCustom } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { CUSTOM_BOUNDARY_ZOOM_LEVEL } from '~/store/boundaryItems/boundaryItems.state';
import { getBoundaryTerritoryGroupMatchupRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { type BoundaryFilterRequest } from '../../../spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import { type ExtendedBoundaryIdentifier } from '../../../store/boundaries/boundaryIdentifier.type';
import { useGetBoundaryTerritoryGroup } from '../../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useBoundaryMetrics } from './useBoundaryMetrics';
import { useMetricsData } from './useMetricsData';

export const useBoundaryMetricsData = (activeMetric: ExtendedBoundaryIdentifier, ignoreFilters?: boolean, entireGroup?: boolean) => {
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const { checkBoundaryIsCustom } = useCheckBoundaryIsCustom();
  const boundaryPolygons = useMapBoundariesSelector();
  const spreadsheetColumns = useSpreadsheetColumns();

  const spreadsheetRowsCountRequest = useMemo(() => {
    if (entireGroup) {
      return Array.from(new Set(spreadsheetColumns.map(column => column.spreadsheetId)));
    }

    return undefined;
  }, [entireGroup, spreadsheetColumns]);

  const boundaryLastUpdateTimestamp = boundaryPolygons.get(activeMetric.boundaryGroupId)?.get(activeMetric.boundaryId)?.lastFetchTimestamp ?? 0;

  const boundaryTerritoryGroup = useMemo(() => {
    return getBoundaryTerritoryGroupById(activeMetric.boundaryTerritoryGroupId);
  }, [getBoundaryTerritoryGroupById, activeMetric.boundaryTerritoryGroupId]);

  const allMetrics = useBoundaryMetrics(boundaryTerritoryGroup);

  // When showing details for the entire boundary group we only support spreadsheet data.
  // Census/demographic metrics are excluded for performance reasons (too many boundaries in one BE call).
  const selectedMetrics = useMemo(() => (
    entireGroup
      ? allMetrics.filter(metric => metric.type === SelectedDataType.SpreadsheetColumn)
      : allMetrics
  ), [allMetrics, entireGroup]);

  const isCustomBoundary = checkBoundaryIsCustom(activeMetric.boundaryGroupId);
  const sendGeometry = !entireGroup && DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID && isCustomBoundary;

  // Only look up the boundary when its geometry is actually sent, so unrelated boundary changes don't re-render.
  const customBoundary = sendGeometry ? boundaryPolygons.get(activeMetric.boundaryGroupId)?.get(activeMetric.boundaryId) : undefined;

  const polygonFilterRequest: PolygonFilterRequest | undefined = useMemo(() => {
    const multiPolygon = customBoundary?.paths.get(CUSTOM_BOUNDARY_ZOOM_LEVEL);

    if (multiPolygon) {
      return {
        type: 'or',
        polygons: multiPolygon.map(polygon => ({ path: convertPolygonToGeometry(polygon) })),
      };
    }

    return undefined;
  }, [customBoundary]);

  // Stabilize the boundary IDs to prevent unnecessary re-renders when other boundary polygons change.
  const activeMetricBoundaryId = useMemo(() => [activeMetric.boundaryId], [activeMetric.boundaryId]);
  const groupBoundaries = entireGroup ? boundaryPolygons.get(activeMetric.boundaryGroupId) : undefined;
  const groupBoundaryIds = useMemo(() => (groupBoundaries ? Array.from(groupBoundaries.keys()) : undefined), [groupBoundaries]);
  const filterBoundaryIds = useMemo(() => (entireGroup ? groupBoundaryIds ?? [] : activeMetricBoundaryId), [entireGroup, groupBoundaryIds, activeMetricBoundaryId]);

  const boundaryFilterRequest: BoundaryFilterRequest | undefined = useMemo(() => {
    // we want to include boundaryLastUpdateTimestamp in the memo so that we refetch new metrics data if the boundary polygon is edited/updated
    if (!boundaryTerritoryGroup || !boundaryLastUpdateTimestamp) {
      return undefined;
    }

    if (sendGeometry) {
      return undefined;
    }

    return {
      type: 'or',
      boundaries: {
        [activeMetric.boundaryGroupId]: {
          boundary_ids: filterBoundaryIds,
          matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
        },
      },
    } satisfies BoundaryFilterRequest;
  }, [activeMetric.boundaryGroupId, boundaryLastUpdateTimestamp, boundaryTerritoryGroup, filterBoundaryIds, sendGeometry]);

  return useMetricsData({
    selectedMetrics,
    boundaryFilterRequest,
    polygonFilterRequest,
    ignoreFilterTree: ignoreFilters,
    spreadsheetRowsCount: spreadsheetRowsCountRequest, // when showing data for the entire boundary group, we need to fetch locations count from the BE. The value is not availabe in the boundary details.
  });
};
